import { Button, Card, Col, Row, Space } from "antd";
import React, { useMemo } from "react";
import { useNotification } from "../../contexts/Notifications";
import { useGetSpacesAndComponentsDataQuery } from "../../generated/types";
import { Project } from "../../types";
import { useDebounce } from "../../utils/react-utils";
import { DisplayTreeNode, DisplayTreeNodeType } from "../DisplayTree/types";
import { SubcontractorsList } from "../SubcontractorsList/SubcontractorsList";
import { AddMappingsButton } from "./AddMappingsButton";
import styles from "./ConfigPlansBottomRibbon.module.css";
import { DeleteMappingsButton } from "./DeleteMappingsButton";
import { EditComponentTagsContainer } from "./EditComponentTagsContainer";
import { EnableDisableMappingsButton } from "./EnableDisableMappingsButton";

import { SPACE_INFO_LOADING_ERROR } from "./messages";

const QUERY_DEBOUNCE_MS = 1000;

interface ConfigPlansBottomRibbonProps {
  project: Project;
  planId: string | undefined;
  checkedSpacesTreeNodes: DisplayTreeNode[];
  checkedComponents: string[];
  onUpdate: (keys: string[]) => void;
}

export const ConfigurationPlansBottomRibbon = (
  props: ConfigPlansBottomRibbonProps,
) => {
  const {
    project,
    planId,
    checkedSpacesTreeNodes,
    checkedComponents,
    onUpdate,
  } = props;

  const preventMappingAction =
    checkedComponents.length === 0 ||
    checkedSpacesTreeNodes.length === 0 ||
    !checkedSpacesTreeNodes.every(
      (node) => node.nodeType === DisplayTreeNodeType.Space,
    );

  const queryVariables = useMemo(
    () =>
      preventMappingAction
        ? null
        : {
            spaceIds: checkedSpacesTreeNodes.map((space) => space.key),
            componentIds: checkedComponents,
          },
    [preventMappingAction, checkedSpacesTreeNodes, checkedComponents],
  );

  const debouncedQueryVariables = useDebounce(
    queryVariables,
    QUERY_DEBOUNCE_MS,
  );

  const {
    data: nodesData,
    error,
    loading,
  } = useGetSpacesAndComponentsDataQuery({
    skip: debouncedQueryVariables === null,
    variables:
      debouncedQueryVariables === null
        ? undefined
        : {
            ...project,
            ...debouncedQueryVariables,
          },
  });

  const notify = useNotification();
  if (error) {
    notify(`${SPACE_INFO_LOADING_ERROR} - ${error}`, "error");
  }

  return (
    <Card className={styles["bottom-ribbon"]} loading={loading}>
      <Row gutter={8}>
        <Col span={22}>
          <Space
            direction={"vertical"}
            className={styles["mapping-action-box"]}
          >
            <Row className={styles["buttons-section"]}>
              <Col span={6}>
                <AddMappingsButton
                  project={project}
                  selectedComponents={nodesData?.componentsByIds ?? []}
                  selectedSpaces={nodesData?.spacesByIds ?? []}
                  disabled={preventMappingAction}
                  onUpdate={onUpdate}
                />
              </Col>
              <Col span={6}>
                <EnableDisableMappingsButton
                  project={project}
                  selectedComponents={nodesData?.componentsByIds ?? []}
                  selectedSpaces={nodesData?.spacesByIds ?? []}
                  updateAction="enable"
                  onUpdate={onUpdate}
                />
              </Col>
              <Col span={6}>
                <EnableDisableMappingsButton
                  project={project}
                  selectedComponents={nodesData?.componentsByIds ?? []}
                  selectedSpaces={nodesData?.spacesByIds ?? []}
                  updateAction="disable"
                  onUpdate={onUpdate}
                />
              </Col>
              <Col span={6}>
                <DeleteMappingsButton
                  project={project}
                  selectedComponents={nodesData?.componentsByIds ?? []}
                  selectedSpaces={nodesData?.spacesByIds ?? []}
                  onUpdate={onUpdate}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Button
                  block
                  className={styles["arrow-left"]}
                  type="primary"
                  disabled={true}
                >
                  Apply mapping action
                </Button>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <SubcontractorsList />
              </Col>
            </Row>
          </Space>
        </Col>
        <Col span={2}>
          <Space className={styles.tags} direction="vertical">
            <EditComponentTagsContainer
              selectedComponents={checkedComponents}
              project={project}
              planId={planId}
            />
          </Space>
        </Col>
      </Row>
    </Card>
  );
};
